import { useMatchRoute } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"

import { useSyncSidebarScroll } from "#React/hooks/useSyncSidebarScroll.js"
import Icon from "#React/ui/Icon"
import KeyboardShortcut from "#React/ui/KeyboardShortcut"
import Tooltip from "#React/ui/Tooltip"
import cn from "#Root/react/utils/cn"
import { isSidebarCollapsed } from "#Utils/sidebarCollapse"

import Link, { type LinkProps } from "../shared/link"
import MobileUtilityNavigation from "./MobileUtilityNavigation"
import ResponsiveSidebar from "./ResponsiveSidebar"
import SidebarCollapseToggle from "./SidebarCollapseToggle"
import { SidebarPopoverProvider, SidebarPopoverTarget, useSidebarPopover } from "./SidebarPopover"
import { SidebarSubmenuPathContext, useSidebarSubmenuState } from "./useSidebarSubmenuState"

type MatchRoute = ReturnType<typeof useMatchRoute>

type MatchDescriptor = {
  to: string
  fuzzy?: boolean
  params?: Record<string, string>
  search?: Record<string, unknown>
}

type NavigationItemProps = Pick<
  LinkProps,
  "href" | "target" | "rel" | "to" | "params" | "search"
> & {
  id?: string
  title: string
  icon?: string
  iconClassName?: string
  keyboardShortcut?: string
  hint?: string
  label?: React.ReactNode
  color?: string
  type?: string
  active?: boolean
  open?: boolean
  nonLink?: boolean
  match?: MatchDescriptor | MatchDescriptor[]
  children?: React.ReactNode
}

type RenderedItemProps = NavigationItemProps & { matchRoute?: MatchRoute }
type PopoverItem = NonNullable<React.ComponentProps<typeof SidebarPopoverTarget>["items"]>[number]

const childrenArray = (children: React.ReactNode): React.ReactNode[] => {
  let result: React.ReactNode[] = []
  if (Array.isArray(children)) {
    for (const child of children) {
      result = result.concat(childrenArray(child))
    }
  } else if (children) {
    result.push(children)
  }
  return result
}

// Does the current URL match any of an item's `match` descriptors?
//
// Each descriptor names a *route*, not a URL:
//
//   { to }                          the route exactly
//   { to, fuzzy: true }             the route or anything below it
//   { to, params: { group: "x" } }  narrowed to specific param values
//   { to, search: { k: "v" } }      requires these search params
//
// `$param` segments need no value — `matchRoute` treats them as single-segment
// wildcards and returns what the URL had there. One descriptor can cover any
// incident or traced action.
//
// Params are a *filter*: omit them to match any value, pass some to require
// those. Search is compared as a subset, so omitting it matches any query
// string (the old `ignoreQueryString` behaviour, now the default).
const matchesAny = (matchRoute: MatchRoute, match?: NavigationItemProps["match"]) => {
  if (!match) {
    return false
  }

  return (Array.isArray(match) ? match : [match]).some(({ to, fuzzy = false, params, search }) =>
    Boolean(matchRoute({ to, fuzzy, ...(params && { params }), ...(search && { search }) })),
  )
}

// An item is active if it says so outright (`active`), or if the current route
// matches one of its `match` descriptors. Items with neither are never active.
//
// There is no walk into `children` any more: a parent that should light up for
// its descendants says so with its own descriptor (usually one `fuzzy` entry),
// rather than inheriting it from invisible child items.
const isActive = (props: NavigationItemProps, matchRoute?: MatchRoute) => {
  if (props.active === true) {
    return true
  }

  return matchRoute ? matchesAny(matchRoute, props.match) : false
}

// `matchRoute` is threaded through rather than obtained here: this is a plain
// function called from `Menu`'s body, so it cannot call the hook itself.
const popoverItems = (
  children: React.ReactNode,
  matchRoute?: MatchRoute,
  scope: string[] = [],
): PopoverItem[] => {
  return childrenArray(children).flatMap<PopoverItem>((child) => {
    if (!React.isValidElement<NavigationItemProps>(child)) return []

    const { type, title, hint, href, to, params, search, children: childChildren } = child.props

    if (type === "subitem" && title && (to || href)) {
      return [{ title, hint, href, to, params, search, active: isActive(child.props, matchRoute) }]
    }

    if (type === "submenu") {
      const nestedItems = popoverItems(childChildren, matchRoute, [...scope, title])

      if (nestedItems.length === 0) return []

      return [{ title, type: "group", items: nestedItems, scope }]
    }

    return []
  })
}

const NavLink = ({
  id,
  title,
  icon,
  iconClassName,
  href = "",
  target,
  rel,
  to,
  params,
  search,
  keyboardShortcut,
  active,
  children,
  open,
  nonLink = false,
}: RenderedItemProps) => {
  const { saveScrollOffset } = useSyncSidebarScroll()
  const submenuId = React.useId()
  const parentPath = React.useContext(SidebarSubmenuPathContext)
  const path = [...parentPath, title]
  const items = popoverItems(children, undefined, path)
  const hasChildren = items.length > 0
  const submenuHidden = !open
  const sidebarPopover = useSidebarPopover()
  const collapsed = sidebarPopover?.isCollapsed() ?? isSidebarCollapsed()
  const content = (
    <>
      {iconClassName ? (
        <i className={iconClassName} aria-hidden="true" />
      ) : (
        <Icon name={`fa ${icon}`} className="c-side-nav__item-icon" />
      )}
      <span className="c-side-nav__item-title responsive-hidden">{title}</span>
      {keyboardShortcut && (
        <div className="responsive-hidden indent-0">
          <KeyboardShortcut shortcut={keyboardShortcut} className="c-side-nav__item-icon ml-1" />
        </div>
      )}
    </>
  )

  return (
    <li id={id} className={cn("c-side-nav__section", active && "c-side-nav__section--active")}>
      <SidebarPopoverTarget title={title} items={items}>
        {nonLink ? (
          <span
            className="c-side-nav__item"
            role={collapsed ? "note" : undefined}
            tabIndex={collapsed ? 0 : undefined}
            aria-label={collapsed ? title : undefined}
          >
            {content}
          </span>
        ) : (
          <Link
            className="c-side-nav__item"
            href={href}
            target={target}
            rel={rel}
            to={to}
            params={params}
            search={search}
            onClick={saveScrollOffset}
            aria-current={active ? "page" : undefined}
            aria-label={iconClassName && collapsed ? title : undefined}
            aria-haspopup={hasChildren ? "dialog" : undefined}
          >
            {content}
          </Link>
        )}
      </SidebarPopoverTarget>
      {hasChildren && (
        <ul
          id={submenuId}
          className={cn(
            "c-side-nav__subitems responsive-sidebar-submenu",
            submenuHidden && "hidden!",
          )}
          hidden={submenuHidden}
          inert={submenuHidden}
        >
          <SidebarSubmenuPathContext.Provider value={path}>
            {children}
          </SidebarSubmenuPathContext.Provider>
        </ul>
      )}
    </li>
  )
}

const SubMenu = ({ id, title, children, active }: RenderedItemProps) => {
  const [isOpen, setIsOpen] = useSidebarSubmenuState(title)
  const parentPath = React.useContext(SidebarSubmenuPathContext)

  // Opens on the false -> true transition only. `active` is derived from the
  // router now, so it no longer needs a location-derived key to re-fire — which
  // means a submenu you collapsed by hand stays collapsed while you navigate
  // inside it, instead of springing open again.
  useEffect(() => {
    if (active) {
      setIsOpen(true)
    }
  }, [active, setIsOpen])

  const handleMenuOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey) return
    setIsOpen(!isOpen)
  }

  return (
    <li id={id}>
      <div className="c-side-nav__subitem gap-x-1" onClick={handleMenuOpen}>
        <span className="c-side-nav__subitem-title">{title}</span>
        <Icon variant="disclosure" open={isOpen} className="c-side-nav__subitem-icon" />
      </div>
      {isOpen && (
        <ul className="ml-0 c-side-nav__subitems">
          <SidebarSubmenuPathContext.Provider value={[...parentPath, title]}>
            {children}
          </SidebarSubmenuPathContext.Provider>
        </ul>
      )}
    </li>
  )
}

const Menu = ({
  id,
  title,
  icon,
  iconClassName,
  children,
  active,
  open,
  matchRoute,
}: RenderedItemProps) => {
  const serverOpen = open !== undefined
  const [isOpen, setIsOpen] = useState(() => {
    return serverOpen
      ? open
      : active || JSON.parse(localStorage.getItem(`side-menu-${title}`) ?? "false") || false
  })
  const parentPath = React.useContext(SidebarSubmenuPathContext)
  const path = [...parentPath, title]
  const items = popoverItems(children, matchRoute, path)
  const sidebarPopover = useSidebarPopover()
  const target = React.useRef<HTMLDivElement>(null)
  const submenuId = React.useId()
  const popoverOpen = sidebarPopover?.isOpenFor(target.current) ?? false
  const collapsed = sidebarPopover?.isCollapsed() ?? isSidebarCollapsed()

  // App menus remember expansion; settings menus start from the Rails-provided state.
  useEffect(() => {
    if (!serverOpen && active) {
      setIsOpen(true)
    }
  }, [active, serverOpen])

  useEffect(() => {
    if (!serverOpen) {
      localStorage.setItem(`side-menu-${title}`, String(isOpen))
    }
  }, [isOpen, serverOpen, title])

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.ctrlKey || e.metaKey) return
    if (sidebarPopover?.isCollapsed() && items.length > 0) return

    setIsOpen(!isOpen)
  }

  const menuItemContent = (
    <>
      {iconClassName ? (
        <i className={iconClassName} aria-hidden="true" />
      ) : (
        <Icon name={`fa ${icon}`} className="c-side-nav__item-icon" />
      )}
      <span className="c-side-nav__item-title responsive-hidden">{title}</span>
      {iconClassName ? (
        <span className="responsive-hidden" aria-hidden="true">
          <i className={cn("far fa-fw min-w-4.25", isOpen ? "fa-angle-up" : "fa-angle-down")} />
        </span>
      ) : (
        <div className="responsive-hidden indent-0">
          <Icon variant="disclosure" open={isOpen} className="c-side-nav__subitem-icon" />
        </div>
      )}
    </>
  )

  const button = (
    <button
      type="button"
      className={
        serverOpen
          ? "c-side-nav__item cursor-pointer"
          : items.length > 0
            ? "c-side-nav__item focus:outline-hidden focus-visible:ring-2 focus-visible:ring-edge-default"
            : "c-side-nav__item"
      }
      aria-expanded={serverOpen ? (collapsed ? popoverOpen : isOpen) : undefined}
      aria-controls={
        serverOpen
          ? collapsed
            ? popoverOpen
              ? sidebarPopover?.popoverId
              : undefined
            : submenuId
          : undefined
      }
      onClick={handleMenuOpen}
    >
      {menuItemContent}
    </button>
  )

  return (
    <li
      id={id}
      className={cn("c-side-nav__section", {
        "c-side-nav__section--open": isOpen,
        "c-side-nav__section--active": active,
        "select-none bg-none": !serverOpen,
      })}
    >
      {items.length > 0 ? (
        <SidebarPopoverTarget title={title} items={items} openOnInteraction targetRef={target}>
          {button}
        </SidebarPopoverTarget>
      ) : (
        button
      )}
      {(serverOpen || isOpen) && (
        <ul
          id={serverOpen ? submenuId : undefined}
          className={cn(
            "c-side-nav__subitems responsive-sidebar-submenu",
            serverOpen && !isOpen && "hidden!",
          )}
          hidden={serverOpen && !isOpen}
          inert={serverOpen && !isOpen}
        >
          <SidebarSubmenuPathContext.Provider value={path}>
            {children}
          </SidebarSubmenuPathContext.Provider>
        </ul>
      )}
    </li>
  )
}

const Subitem = ({
  id,
  title,
  label,
  hint,
  href = "",
  to,
  params,
  search,
  active,
}: RenderedItemProps) => {
  const { saveScrollOffset } = useSyncSidebarScroll()

  const link = (
    <Link
      className={cn("c-side-nav__subitem", active && "c-side-nav__subitem--active")}
      href={href}
      to={to}
      params={params}
      search={search}
      onClick={saveScrollOffset}
      aria-current={active ? "page" : undefined}
    >
      <span className="c-side-nav__subitem-title">
        {title}
        {label && (
          <div className="inline-flex justify-center items-center ml-1 w-5 h-5 p-0">{label}</div>
        )}
      </span>
    </Link>
  )

  return (
    <li id={id}>
      {hint ? (
        <Tooltip>
          <Tooltip.Trigger>{link}</Tooltip.Trigger>
          <Tooltip.Content side="right">{hint}</Tooltip.Content>
        </Tooltip>
      ) : (
        link
      )}
    </li>
  )
}

// Each type maps to a component so that only the matched one renders, and only
// its hooks run. These used to be plain functions gathered in an eager object
// literal, which meant `Menu`'s and `SubMenu`'s state and effects executed for
// *every* nav item whatever its type — writing a `side-menu-<title>` and a
// `side-menu-sub-<title>` localStorage entry for each one.
const RENDERERS: Record<
  "link" | "menu" | "subitem" | "submenu",
  React.ComponentType<RenderedItemProps>
> = {
  link: NavLink,
  menu: Menu,
  subitem: Subitem,
  submenu: SubMenu,
}

const RouteMatchedSideNavigation = (props: NavigationItemProps) => {
  const matchRoute = useMatchRoute()
  return (
    <SideNavigationItem {...props} matchRoute={matchRoute} active={isActive(props, matchRoute)} />
  )
}

const SideNavigationItem = (props: RenderedItemProps) => {
  const { type } = props
  const Renderer = type && type in RENDERERS ? RENDERERS[type as keyof typeof RENDERERS] : NavLink

  return <Renderer {...props} />
}

const SideNavigationList = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <ul
      className={cn("c-side-nav my-2 lg:my-4 grow w-full min-h-0 overflow-y-auto pb-4", className)}
    >
      {children}
    </ul>
  )
}

const NavigationItem = (props: NavigationItemProps) => {
  if (props.match) {
    return <RouteMatchedSideNavigation {...props} />
  }

  return <SideNavigationItem {...props} active={props.active === true} />
}

type SideNavigationProps = {
  children: React.ReactNode
  footerItems?: React.ReactNode
  mobileHeader?: React.ReactNode
  organizationSlug?: string
}

const SideNavigation = ({
  children,
  footerItems = null,
  mobileHeader = null,
  organizationSlug,
}: SideNavigationProps) => {
  return (
    <ResponsiveSidebar
      organizationSlug={organizationSlug}
      renderContent={(mobile) => (
        <SidebarPopoverProvider collapsed={mobile ? false : undefined}>
          {mobile && mobileHeader}
          <div
            id={mobile ? undefined : "side-nav-scroll-area"}
            className="flex flex-col h-full px-3 overflow-hidden"
          >
            {children}
            <ul className="c-side-nav sticky bottom-0 shrink-0 w-[calc(100%+1.5rem)] -mx-3 p-3 space-y-1 bg-surface-default border-t z-10 mt-auto">
              {mobile && <MobileUtilityNavigation organizationSlug={organizationSlug} />}
              {footerItems && mobile && <li className="-mx-3 border-t my-3" />}
              {footerItems}
              {!mobile && <SidebarCollapseToggle />}
            </ul>
          </div>
        </SidebarPopoverProvider>
      )}
    />
  )
}

SideNavigation.Item = NavigationItem
SideNavigation.List = SideNavigationList

export default SideNavigation
